import axios from 'axios';
import {
  getLoginMessageForReason,
  persistLoginBanner,
} from '../utils/loginMessages';

const API_URL = process.env.REACT_APP_API_URL || 'https://ginghamtree.com/api';

console.log('Using API URL:', API_URL); // Helpful for debugging

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Always send cookies
});

// FestivalContext sets this. When non-null, every request (GET, POST, etc.)
// gets ?festivalId=<id> appended unless one is already present. Routes that
// don't care about a festival simply ignore the param.
let activeFestivalIdGetter = () => null;
export const setFestivalIdGetter = (getter) => {
  activeFestivalIdGetter = typeof getter === 'function' ? getter : () => null;
};

api.interceptors.request.use((config) => {
  const fid = activeFestivalIdGetter();
  if (fid && !config.url?.startsWith('/festivals')) {
    config.params = { festivalId: fid, ...(config.params || {}) };
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Only redirect to login if we're not already there and it's a 401
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      // Check if the request was for /auth/me to avoid redirect loops
      if (!error.config.url.includes('/auth/me')) {
        persistLoginBanner(getLoginMessageForReason('session_expired'));
        window.location.href = '/login?reason=session_expired';
      }
    }
    return Promise.reject(error);
  }
);

export const auth = {
  async signup(userData) {
    try {
      const response = await api.post('/auth/signup', userData);
      return response.data.data || response.data;
    } catch (error) {
      // Ensure we pass through the error response data
      if (error.response?.data) {
        error.response.data = {
          error: error.response.data.error || error.response.data.message,
          ...error.response.data
        };
      }
      throw error;
    }
  },

  async login(credentials) {
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data.data || response.data;
    } catch (error) {
      // Ensure we pass through the error response data
      if (error.response?.data) {
        error.response.data = {
          error: error.response.data.error || error.response.data.message,
          ...error.response.data
        };
      }
      throw error;
    }
  },

  logout() {
    console.log('Starting logout process...');
    // Call backend to clear the cookie
    return api.post('/auth/logout')
      .then((response) => {
        console.log('Logout API call successful:', response.data);
        // Don't redirect here - let the AuthContext handle it
        // Clear any local state/storage if needed
        return Promise.resolve();
      })
      .catch((error) => {
        console.error('Logout API error:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            withCredentials: error.config?.withCredentials
          }
        });
        // Return a resolved promise even if the API call fails
        // The cookie clearing should still work
        return Promise.resolve();
      });
  },

  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');
      console.log('Current user data:', response.data.data);
      return response.data.data;
    } catch (error) {
      // Don't redirect for /auth/me calls
      if (error.response?.status === 401) {
        return null;
      }
      throw error;
    }
  },
};

export const booths = {
  async getAvailableBooths() {
    const response = await api.get('/booths/available');
    return response.data;
  },

  async registerBooth(boothId) {
    const response = await api.post('/booths/register', { boothId });
    return response.data;
  },

  async getMyBooths() {
    const response = await api.get('/booths/my-booths');
    return response.data;
  },
};

export const mapApi = {
  async get(festivalId) {
    const response = await api.get('/map', { params: { festivalId } });
    return response.data;
  },
  async updateCell(cellId, data) {
    const response = await api.put(`/map/${cellId}`, data);
    return response.data;
  },
  async batchUpdate(ids, data) {
    const response = await api.put('/map/batch/ids', { ids, data });
    return response.data;
  },
  async expandRows(festivalId, count = 1, position = null) {
    const response = await api.post(
      '/map/expand-rows',
      { count, position },
      { params: { festivalId } }
    );
    return response.data;
  },
  async expandColumns(festivalId, count = 1, position = null) {
    const response = await api.post(
      '/map/expand-columns',
      { count, position },
      { params: { festivalId } }
    );
    return response.data;
  },
  landmarks: {
    async list(festivalId) {
      const response = await api.get('/map/landmarks', { params: { festivalId } });
      return response.data;
    },
    async create(festivalId, payload) {
      const response = await api.post('/map/landmarks', payload, { params: { festivalId } });
      return response.data;
    },
    async update(id, payload) {
      const response = await api.put(`/map/landmarks/${id}`, payload);
      return response.data;
    },
    async remove(id) {
      const response = await api.delete(`/map/landmarks/${id}`);
      return response.data;
    },
  },
};

export const festivals = {
  async list() {
    const response = await api.get('/festivals');
    return response.data;
  },
  async getActive() {
    const response = await api.get('/festivals/active');
    return response.data;
  },
  async get(id) {
    const response = await api.get(`/festivals/${id}`);
    return response.data;
  },
  async create(payload) {
    const response = await api.post('/festivals', payload);
    return response.data;
  },
  async update(id, payload) {
    const response = await api.put(`/festivals/${id}`, payload);
    return response.data;
  },
  async activate(id) {
    const response = await api.post(`/festivals/${id}/activate`);
    return response.data;
  },
  async archive(id) {
    const response = await api.post(`/festivals/${id}/archive`);
    return response.data;
  },
  async openRegistration(id) {
    const response = await api.post(`/festivals/${id}/open-registration`);
    return response.data;
  },
  async closeRegistration(id) {
    const response = await api.post(`/festivals/${id}/close-registration`);
    return response.data;
  },
  async cloneMap(targetId, sourceId) {
    const response = await api.post(`/festivals/${targetId}/clone-map`, { sourceFestivalId: sourceId });
    return response.data;
  },
  async summary(id) {
    const response = await api.get(`/festivals/${id}/summary`);
    return response.data;
  },
};

export const categories = {
  async list({ includeInactive = false } = {}) {
    const response = await api.get('/categories', {
      params: includeInactive ? { includeInactive: 'true' } : {},
    });
    return response.data;
  },
  async create(payload) {
    const response = await api.post('/categories', payload);
    return response.data;
  },
  async update(id, payload) {
    const response = await api.put(`/categories/${id}`, payload);
    return response.data;
  },
  async remove(id) {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export default api;
